// libs
import * as React from "react";
import {
  Grid,
  Link,
  InputLabel,
  MenuItem,
  Card,
  FormControl,
  Select,
  Button,
  Box,
  Stack,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import ImportExportIcon from "@mui/icons-material/ImportExport";

// settings
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";

// helpers import
import { formatToUsDate, formatToUsDateFromUTC } from "../utils/DateHelper";
import { toTitleCase } from "../utils/CaseFormatHelper";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import DatePickerWithLabel from "../common/DatePickerWithLabel";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

interface Props {}

const ImmunizationForecastReport: React.FC<Props> = (props: any) => {
  const authenticationSetting = React.useContext(AuthenticationContext);

  // first mount  controller
  const isInitialMount = React.useRef(true);

  // other states
  const [customer, setCustomer] = React.useState<any>("");
  const [insuranceType, setInsuranceType] = React.useState<any>("");
  const [imType, setImType] = React.useState<any>("");
  const [startDate, setStartDate] = React.useState<string>(
    moment().subtract(1, "month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = React.useState<string>(
    moment().format("YYYY-MM-DD")
  );

  // data
  const [imData, setImData] = React.useState<any>([]);

  // filter options
  const [customerOptions, setCustomerOptions] = React.useState<any>();
  const [insuranceTypeOptions, setInsuranceTypeOptions] = React.useState<any>();
  const [imTypeOptions, setImTypeOptions] = React.useState<any>();

  const columns: GridColDef[] = [
    {
      field: "",
      headerName: "",
      width: 40,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "name",
      headerName: "Organization Name",
      disableColumnMenu: true,
      minWidth: 200,
    },
    {
      field: "customer_county",
      headerName: "Organization County",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "first_name",
      headerName: "First Name",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "middle_name",
      headerName: "Middle Name",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "date_of_birth",
      headerName: "Date of Birth",
      description: "Date of Birth",
      width: 170,
      disableColumnMenu: true,
      valueFormatter: (params) => {
        return formatToUsDateFromUTC(params);
      },
    },
    {
      field: "gender",
      headerName: "Gender",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "mrn_number",
      headerName: "MRN",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "address",
      headerName: "Address",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "address_line_2",
      headerName: "Address Line 2",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "city",
      headerName: "City",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "state",
      headerName: "State",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "zip",
      headerName: "ZIP Code",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "county",
      headerName: "County",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "race",
      headerName: "Race",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "ethnicity",
      headerName: "Ethnicity",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "insurance_type",
      headerName: "Insurance Type",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "plan_name",
      headerName: "Insurance Name",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "insured_id",
      headerName: "Insurance ID#",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "prov_name",
      headerName: "Provider Name",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "vaccine_type",
      headerName: "Imunization Type",
      type: "string",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "recommended_date",
      headerName: "Immunization Due Date",
      type: "string",
      width: 200,
      disableColumnMenu: true,
      valueFormatter: (params) => {
        return formatToUsDateFromUTC(params);
      },
    },
    {
      field: "given_date",
      headerName: "Immunization Given Date",
      type: "string",
      width: 200,
      disableColumnMenu: true,
      valueFormatter: (params) => {
        if (params) {
          return formatToUsDateFromUTC(params);
        }
      },
    },
    {
      field: "defer_date",
      headerName: "Immunization Deferred Date",
      type: "string",
      width: 200,
      disableColumnMenu: true,
      valueFormatter: (params) => {
        if (params) {
          return formatToUsDateFromUTC(params);
        }
      },
    },
  ];

  React.useEffect(() => {
    getAssets();
  }, []);

  React.useEffect(() => {
    getData();
  }, [customer, insuranceType, imType]);

  const getAssets = () => {
    fetch(`/admin/reports/im_forecasts/assets`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.error);
        } else {
          setCustomerOptions(result?.resource?.customers);
          setInsuranceTypeOptions(result?.resource?.insurance_types);
          setImTypeOptions(result?.resource?.im_types);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const sanitizeDate = (date) => {
    // first load gives us string date, subsequesnt load gives us other date so we sanitizing
    if (typeof date == "string") {
      return new Date(date);
    }
    return date;
  };

  const getData = () => {
    fetch(
      `/admin/reports/im_forecasts/report_data?` +
        new URLSearchParams({
          customer_id: customer,
          im_type: imType,
          insurance_type: insuranceType,
          start_date: startDate ? startDate : "",
          end_date: endDate ? endDate : "",
        }),
      {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      }
    )
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.error);
        } else {
          setImData(result?.resource);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleSearchSave = () => {
    getData();
  };

  return (
    <div className="main-content-outer">
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        className="main-content"
        sx={{ paddingLeft: "138px" }}
      >
        <Grid
          item
          xs={12}
          id="provider-action-report"
          className="patient-edit-container patient-edit-form provider-action-report"
          style={{
            maxHeight: "calc(100vh - 120px)",
            height: "calc(100vh - 120px)",
            paddingBottom: "0px",
          }}
        >
          <Grid container>
            <Grid
              className="patient-edit-header"
              container
              justifyContent="space-between"
            >
              <Grid item xs={5}>
                <Stack direction="row" columnGap="12px">
                  <Link
                    style={{
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                    }}
                    href={`/reports`}
                  >
                    <ArrowBackIosNewIcon
                      style={{
                        borderRadius: "3px",
                        padding: "8px",
                        color: "black",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        fontSize: "14px",
                      }}
                    />
                  </Link>
                  <h3>Immunization Forecaster Detail Report</h3>
                </Stack>
              </Grid>
            </Grid>
            <Grid
              container
              className="form-container"
              style={{ paddingTop: "15px", paddingRight: "40px" }}
            >
              {/* column 1 */}
              <Grid item xs={2} className="left-column">
                <h3> Date Range </h3>

                <p>Immunizaton Forecast Due Date</p>

                <Grid item xs={12} className="field-container">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Box
                      sx={{
                        width: "100%",
                        paddingY: "4px",
                      }}
                    >
                      <DatePickerWithLabel
                        date={startDate}
                        setDate={setStartDate}
                        fontSize={14}
                        background="white"
                        initialDate={moment()
                          .subtract(1, "month")
                          .format("YYYY-MM-DD")}
                      />
                    </Box>
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} className="field-container">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Box
                      sx={{
                        width: "100%",
                        paddingY: "4px",
                      }}
                    >
                      <DatePickerWithLabel
                        date={endDate}
                        setDate={setEndDate}
                        fontSize={14}
                        background="white"
                        initialDate={moment().format("YYYY-MM-DD")}
                      />
                    </Box>
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <Button
                    className="basic-button"
                    onClick={handleSearchSave}
                    style={{ height: "40px" }}
                  >
                    Search
                  </Button>
                </Grid>

                <div className="divider-orange"></div>

                <h3> Filters </h3>

                <Grid item xs={12} className="field-container">
                  <FormControl variant="standard" style={{ width: "100%" }}>
                    <InputLabel
                      id="demo-simple-select-helper-label"
                      htmlFor="customer_id"
                    >
                      Organization
                    </InputLabel>
                    <Select
                      labelId="customer_id"
                      id="customer_id"
                      value={customer}
                      label="Organization"
                      onChange={(event) => {
                        setCustomer(event.target.value);
                      }}
                      style={{ width: "100%", marginLeft: 0 }}
                    >
                      <MenuItem
                        aria-label="None"
                        value=""
                        style={{ padding: 10 }}
                      >
                        Select
                      </MenuItem>
                      {customerOptions?.map((customer, index) => (
                        <MenuItem
                          key={index}
                          value={customer.id}
                          style={{ padding: 10 }}
                        >
                          {customer.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={12}
                  className="field-container"
                  style={{ marginTop: "20px" }}
                >
                  <FormControl variant="standard" style={{ width: "100%" }}>
                    <InputLabel
                      id="demo-simple-select-helper-label"
                      htmlFor="insurance_type"
                    >
                      Insurance Type
                    </InputLabel>
                    <Select
                      labelId="insurance_type"
                      id="insurance_type"
                      value={insuranceType}
                      label="Insurance Type"
                      onChange={(event) => {
                        setInsuranceType(event.target.value);
                      }}
                      style={{ width: "100%", marginLeft: 0 }}
                    >
                      <MenuItem
                        aria-label="None"
                        value=""
                        style={{ padding: 10 }}
                      >
                        Select
                      </MenuItem>
                      {insuranceTypeOptions?.map((ito, index) => (
                        <MenuItem
                          key={index}
                          value={ito}
                          style={{ padding: 10 }}
                        >
                          {ito}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={12}
                  className="field-container"
                  style={{ marginTop: "20px" }}
                >
                  <FormControl variant="standard" style={{ width: "100%" }}>
                    <InputLabel
                      id="demo-simple-select-helper-label"
                      htmlFor="im_type"
                    >
                      Immunization Type
                    </InputLabel>
                    <Select
                      labelId="im_type"
                      id="im_type"
                      value={imType}
                      label="Action Status"
                      onChange={(event) => {
                        setImType(event.target.value);
                      }}
                      style={{ width: "100%", marginLeft: 0 }}
                    >
                      <MenuItem
                        aria-label="None"
                        value=""
                        style={{ padding: 10 }}
                      >
                        Select
                      </MenuItem>
                      {imTypeOptions?.map((ito, index) => (
                        <MenuItem
                          key={index}
                          value={ito}
                          style={{ padding: 10, textTransform: "capitalize" }}
                        >
                          {toTitleCase(ito)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {/* column 2 */}
              <Grid item xs={10} className="right-column">
                {/* overview row */}
                <Grid container>
                  <Grid item xs={12} className="shadowed-box">
                    <Grid container>
                      <Grid
                        className="patient-edit-header"
                        container
                        justifyContent="space-between"
                      >
                        <Grid item xs={4}>
                          <p className="secondary-label"> Report Details </p>
                        </Grid>
                        <Grid item xs={1}>
                          <Link
                            href={`/admin/reports/im_forecasts/generate_csv.csv?${new URLSearchParams(
                              {
                                customer_id: customer,
                                im_type: imType,
                                insurance_type: insuranceType,
                                start_date: startDate ? startDate : "",
                                end_date: endDate ? endDate : "",
                              }
                            )}`}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              marginTop: "10px",
                            }}
                          >
                            <span>
                              <ImportExportIcon />
                            </span>
                            <span>Export CSV</span>
                          </Link>
                        </Grid>
                      </Grid>
                      <Grid className="action-table-container">
                        <div
                          style={{
                            height: "calc(100vh - 305px)",
                            width: "100%",
                          }}
                        >
                          {imData?.length >= 1 && (
                            <DataGrid
                              rows={imData}
                              columns={columns}
                              initialState={{
                                pagination: {
                                  paginationModel: { pageSize: 20 },
                                },
                              }}
                              rowHeight={78}
                              pagination={true}
                            />
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default ImmunizationForecastReport;
